// Admin Groups
export const IDM_ADMIN_USERS_GROUP = 'XAZ-AZTRANSLATE_PORTAL_ADMINISTRATORS';
export const IDM_SUPPORT_MEMBERS_GROUP = 'XAZ-AZ_Translate_Vendors_Admin_Support';
export const IDM_NGS_TEST_ADMIN = 'XAZ-AZTRANSLATE_NGS_DEV-TEST_ADMIN';

export type AdGroupsClaim = string | string[] | undefined | null;

/**
 * Parse AD groups claim from JWT payload.
 * Supports either string or array payloads.
 *
 * Known string formats:
 * - "[group1, group2]" (bracket/comma)
 * - "group1 group2" (space separated)
 */
export function parseAdGroupsClaim(groups: AdGroupsClaim): string[] {
  if (!groups) return [];

  if (Array.isArray(groups)) {
    return groups
      .flatMap((g) => (typeof g === 'string' ? [g] : []))
      .map((g) => g.trim())
      .filter(Boolean);
  }

  const raw = String(groups).trim();
  if (!raw) return [];

  // "[group1, group2]" -> ["group1", "group2"]
  if (raw.startsWith('[') && raw.endsWith(']')) {
    const inner = raw.substring(1, raw.length - 1).trim();
    if (!inner) return [];

    return inner
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean);
  }

  // Default: split by whitespace
  return raw
    .split(/\s+/)
    .map((g) => g.trim())
    .filter(Boolean);
}

/**
 * Check if user is an admin user (full admin rights)
 * @param groups - Array of user's AD groups from JWT token
 * @returns true if user has admin rights
 */
export function isAdminUser(groups: string[] = []): boolean {
  return groups.includes(IDM_ADMIN_USERS_GROUP) || groups.includes(IDM_NGS_TEST_ADMIN);
}

/**
 * Check if user is a support member
 * @param groups - Array of user's AD groups from JWT token
 * @returns true if user is support member
 */
export function isSupportMember(groups: string[] = []): boolean {
  return groups.includes(IDM_SUPPORT_MEMBERS_GROUP);
}

/**
 * Check if user has access to admin module (admin OR support)
 * @param groups - Array of user's AD groups from JWT token
 * @returns true if user can access admin features
 */
export function hasAccessToAdminModule(groups: string[] = []): boolean {
  return isAdminUser(groups) || isSupportMember(groups);
}
